/**
 * Centralized Error Handling Utility
 * Provides consistent error logging across the application
 * Can be extended to integrate with external error tracking services (Sentry, LogRocket, etc.)
 * 
 * @module utils/errorHandler
 */

/**
 * Handle errors with context information for debugging
 * 
 * @param {Error|Object|string} error - The error object, Supabase error, or error message
 * @param {string} [context='Unknown'] - Context describing where the error occurred
 * @param {Function} [showToast=null] - Optional toast function to display error to user
 * @returns {string} - The extracted error message
 * 
 * @example
 * handleError(error, 'Loading garages');
 * handleError(error, 'Updating profile', showToast);
 */
export const handleError = (error, context = 'Unknown', showToast = null) => {
  const errorMessage = error?.message || error || 'Unknown error occurred';
  
  // Log to console with context
  console.error(`[${context}]:`, error);

  // Show toast notification if available
  if (showToast && typeof showToast === 'function') {
    showToast(errorMessage, 'error');
  }

  // TODO: Add external error logging service integration here
  // e.g., Sentry, LogRocket, etc.

  return errorMessage;
};

/**
 * Wrapper for async functions with automatic error handling
 * Simplifies error handling in async operations
 * 
 * @param {Function} asyncFn - Async function to execute
 * @param {string} context - Context for error logging
 * @param {Function} [onError] - Optional callback fired on error
 * @returns {Promise<*>} - Result of asyncFn
 * @throws {Error} - Re-throws the error after handling
 * 
 * @example
 * const data = await handleAsyncError(
 *   () => supabase.from('garages').select('*'),
 *   'Loading garages',
 *   (message) => setError(message)
 * );
 */
export const handleAsyncError = async (asyncFn, context, onError) => {
  try {
    return await asyncFn();
  } catch (error) {
    const message = handleError(error, context);
    if (onError) {
      onError(message);
    }
    throw error;
  }
};
